import { faFish, faMagnifyingGlass, faRotate, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'flowbite-react';
import React, { Fragment, FunctionComponent, useContext, useState } from 'react';
import { Stack } from '../../../../server/src/models';
import { trpc } from '../../../utils/trpc';
import AreYouSure from '../../AreYouSure';
import { EnvironmentContext } from '../context';
import ActionButton from './ActionButton';

export type ServerCardProps = {
  stack: Stack;
};

export const ServerCard: FunctionComponent<ServerCardProps> = ({ stack }) => {
  const ctx = useContext(EnvironmentContext);
  const { updateStack, deleteStack } = ctx.actions;
  const [showConfirm, setShowConfirm] = useState(false);
  const [updateInProgress, setUpdateInProgress] = useState(false);

  const dockerContainers = trpc.container.list.useQuery(ctx.state.env.stack, {
    enabled: ctx.state.env?.stack.id !== undefined,
    refetchInterval: (data) => {
      if (!data) return 500;
      if (data.length < 6) return 500;
      if (data.length > 6) return 1000;
      if (updateInProgress) return 500;
      let allRunning = true;
      data.forEach((item) => {
        if (item.State !== 'running') allRunning = false;
      });
      return allRunning ? 10000 : 1000;
    },
    refetchIntervalInBackground: true
  });

  const termInst = () => {
    setShowConfirm(false);
    deleteStack(stack);
  };

  const getContainerTag = (name: string): string => {
    if (name.includes('bioplan-api') || name.includes('bioplan-worker')) {
      return stack.backendImage.tag;
    } else if (name.includes('frontend')) {
      return stack.frontendImage.tag;
    }
    return '';
  };

  const utilityButtonClass =
    'mr-2 text-xs font-medium text-gray-900 !focus:outline-none rounded-lg hover:text-red-500 focus:z-10 focus:ring-4 focus:ring-gray-200';

  const spinningUtilityButtonClass = `animate-spin ${utilityButtonClass}`;

  const callUpdateStack = (stack: Stack) => {
    setUpdateInProgress(true);
    setTimeout(() => {
      setUpdateInProgress(false);
    }, 6000);
    updateStack(stack);
  };

  return (
    <Fragment>
      <div className="p-6 w-96 bg-white rounded-lg border border-gray-200 shadow-md">
        <ActionButton stack={stack} />
        <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 capitalize">Bioplan</h5>
        {stack.stackState !== 'NOT_CREATED' && stack.stackState !== 'DELETED' && (
          <>
            <h6 className="mb-2 text-sm font-bold uppercase tracking-tight text-gray-900">Instances</h6>
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th scope="col">STACK</th>
                  <th scope="col">STATE</th>
                  <th scope="col">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white border-b">
                  <td>
                    <a
                      href={`https://mgr.bioplan.ai/#!/7/docker/stacks/${stack.name}?id=${stack.id}&type=1&regular=true&external=false&orphaned=false`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="no-underline hover:underline"
                    >
                      {stack.name}
                    </a>
                  </td>
                  <td>{stack.stackState}</td>
                  <td className="flex flex-row justify-end">
                    {stack.stackState === 'ACTIVE' && (
                      <>
                        <Tooltip content="Update stack" style="light">
                          <button
                            type="button"
                            onClick={() => callUpdateStack(stack)}
                            className={updateInProgress ? spinningUtilityButtonClass : utilityButtonClass}
                          >
                            <FontAwesomeIcon icon={faRotate} />
                          </button>
                        </Tooltip>
                      </>
                    )}
                    {stack.stackState === 'ACTIVE' && (
                      <>
                        <Tooltip content="Bioplan Logs" style="light">
                          <button
                            type="button"
                            onClick={() =>
                              window.open(
                                `https://my.papertrailapp.com/groups/39009679/events?q=program%3A${stack.name}`,
                                '_blank',
                                'noreferrer'
                              )
                            }
                            className={utilityButtonClass}
                          >
                            <FontAwesomeIcon icon={faMagnifyingGlass} />
                          </button>
                        </Tooltip>
                      </>
                    )}
                    {stack.stackState === 'ACTIVE' && (
                      <>
                        <Tooltip content="Stocker Logs" style="light">
                          <button
                            type="button"
                            onClick={() =>
                              window.open(
                                'https://logs.bioplan.ai/app/discover#/view/3f5294a0-1c88-11ed-b055-e1a493824e26',
                                '_blank',
                                'noreferrer'
                              )
                            }
                            className={utilityButtonClass}
                          >
                            <FontAwesomeIcon icon={faFish} />
                          </button>
                        </Tooltip>
                      </>
                    )}
                    {stack.stackState === 'INACTIVE' && (
                      <Tooltip content="Terminate instance" style="light">
                        <button type="button" onClick={() => setShowConfirm(true)} className={utilityButtonClass}>
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </Tooltip>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </>
        )}
        {stack.stackState === 'ACTIVE' && dockerContainers && (
          <>
            <h6 className="pt-4 mb-2 text-sm font-bold uppercase tracking-tight text-gray-900">Components</h6>
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th scope="col">NAME</th>
                  <th scope="col">STATE</th>
                  <th scope="col">TAG</th>
                </tr>
              </thead>
              <tbody>
                {dockerContainers.isSuccess &&
                  dockerContainers.data.length > 0 &&
                  dockerContainers.data.map((container) => (
                    <tr key={container.Id} className="bg-white border-b">
                      <td>{container.Labels['com.docker.swarm.service.name'].split('_')[1]}</td>
                      <td>{container.State}</td>
                      <td className={container.OutOfDate ? 'text-red-700' : ''}>
                        {getContainerTag(container.Names[0])}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      <AreYouSure visible={showConfirm} yes={termInst} no={() => setShowConfirm(false)} message="stack" />
    </Fragment>
  );
};
